// Every way to play, turned into the same small set of button names:
// left, right, down, drop, cw, ccw, start, back. A key or button can carry
// more than one name.
//
// Up drops, down falls faster while held. (For an hour on 2026-10-01 down
// dropped too; Matt then: "I realized that the up arrow was drop and down
// arrow was move down. I think you were right to have both of those. So
// can you put that back?")
//
// A "device" is one person's controls: the keyboard or one gamepad. Steam's Remote Play Together shows remote friends to
// the game as extra controllers, so this same code is what remote play will
// run on.

// One keyboard is one player, and both sets of keys work - WASD and the
// arrows, whichever hand you like. (It used to be two players, WASD side
// and arrows side; Matt, 2026-10-01: "we're having some real trouble with
// the left and right sides of the keyboard. Let's do away with that ...
// They don't need to decide which one.")
export const KEYBOARD_LAYOUTS = {
  keys: {
    label: 'Keyboard',
    hint: 'Arrows or WASD · ↓/S down a row · ↑/W turn · Q or / turn back · Space or Enter drop',
    keys: {
      KeyA: 'left',
      KeyD: 'right',
      KeyS: 'down',
      KeyW: 'cw',
      KeyE: 'cw',
      KeyQ: ['ccw', 'back'],
      ArrowLeft: 'left',
      ArrowRight: 'right',
      ArrowDown: 'down',
      ArrowUp: 'cw',
      Slash: ['ccw', 'back'],
      Period: ['ccw', 'back'],
      Space: 'drop',
      Enter: 'drop',
      NumpadEnter: 'drop',
    },
  },
};

// The browser's "standard" gamepad mapping (Xbox layout names; a
// PlayStation or Switch pad reports the same positions).
//
// Turning was swapped after the first real playtest (Matt, 2026-10-01: "it
// feels like the pieces are rotating backwards for me. So can you just
// swap the clockwise, counterclockwise buttons?"): A/Y now turn
// anticlockwise, B/X clockwise. B is ALSO "back" (leave the lobby, hold to
// leave a paused game) - that's a separate name so the swap didn't move it.
const PAD_BUTTONS = {
  0: 'ccw', // A / cross / B on Switch
  1: ['cw', 'back'], // B / circle
  2: 'cw', // X / square
  3: 'ccw', // Y / triangle
  9: 'start', // Menu / Options / +
  12: 'drop', // d-pad up drops
  13: 'down', // d-pad down: one row down per tap
  14: 'left',
  15: 'right',
};
const STICK = 0.5;

// Snapshot of which buttons each device is holding right now.
export function createDevices() {
  // `tapped` remembers keys that went down since the last read, even if
  // they were already released: a quick tap can start and end between two
  // frames, and it must still count as a press.
  const keyboard = { held: new Set(), tapped: new Set() };
  const onDown = (e) => {
    if (e.target?.closest?.('textarea, input')) return;
    const known = Object.values(KEYBOARD_LAYOUTS).some((l) => l.keys[e.code]) || e.code === 'Escape';
    if (known) e.preventDefault();
    keyboard.held.add(e.code);
    if (!e.repeat) keyboard.tapped.add(e.code);
  };
  const onUp = (e) => { keyboard.held.delete(e.code); };
  const onBlur = () => keyboard.held.clear();
  window.addEventListener('keydown', onDown);
  window.addEventListener('keyup', onUp);
  window.addEventListener('blur', onBlur);

  function read() {
    const devices = {};
    for (const [id, layout] of Object.entries(KEYBOARD_LAYOUTS)) {
      const buttons = new Set();
      for (const [code, button] of Object.entries(layout.keys)) {
        if (keyboard.held.has(code) || keyboard.tapped.has(code)) [].concat(button).forEach((b) => buttons.add(b));
      }
      // Escape pauses.
      if (keyboard.held.has('Escape') || keyboard.tapped.has('Escape')) buttons.add('start');
      devices[id] = buttons;
    }
    keyboard.tapped.clear();
    const pads = navigator.getGamepads ? navigator.getGamepads() : [];
    for (const pad of pads) {
      if (!pad || !pad.connected) continue;
      const buttons = new Set();
      pad.buttons.forEach((b, i) => {
        if (b.pressed && PAD_BUTTONS[i]) [].concat(PAD_BUTTONS[i]).forEach((name) => buttons.add(name));
      });
      const [ax = 0, ay = 0] = pad.axes;
      if (ax < -STICK) buttons.add('left');
      if (ax > STICK) buttons.add('right');
      if (ay > STICK) buttons.add('down');
      devices[`pad-${pad.index}`] = buttons;
    }
    return devices;
  }

  function destroy() {
    window.removeEventListener('keydown', onDown);
    window.removeEventListener('keyup', onUp);
    window.removeEventListener('blur', onBlur);
  }

  return { read, destroy };
}

export function deviceLabel(id) {
  if (KEYBOARD_LAYOUTS[id]) return KEYBOARD_LAYOUTS[id].label;
  const index = Number(id.replace('pad-', ''));
  const pad = navigator.getGamepads?.()[index];
  const name = pad?.id ? pad.id.replace(/\s*\(.*\)\s*/g, ' ').trim() : '';
  return name ? `Controller ${index + 1} · ${name.slice(0, 28)}` : `Controller ${index + 1}`;
}

export function deviceHint(id) {
  if (KEYBOARD_LAYOUTS[id]) return KEYBOARD_LAYOUTS[id].hint;
  return 'D-pad or stick move · down: a row · up drops · A/Y turn left · B/X turn right';
}
